import React, { useCallback, useEffect, useState } from "react";
import api from "../../lib/api";
import { fmtNumber } from "../../lib/utils";
import { HeartPulse, ChevronRight, AlertTriangle, FileBarChart2, ChevronDown, ChevronUp } from "lucide-react";

const HEALTH_BAND = (score) => {
  if (score >= 80) return { label: "Healthy", pill: "pill-success", color: "#10B981" };
  if (score >= 60) return { label: "Watch", pill: "pill-warn", color: "#F59E0B" };
  return { label: "At risk", pill: "pill-error", color: "#EF4444" };
};

export default function TenantsHealth() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("");
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get("/platform/tenants-health", { params: period ? { period } : {} });
    setData(r.data);
  }, [period]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="p-6 text-ink-soft">Loading tenant health…</div>;

  return (
    <div className="p-6 space-y-5" data-testid="tenants-health-root">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="section-label">Platform Admin · Tenant operations</div>
          <h1 className="text-2xl font-bold tracking-tight mt-1 flex items-center gap-2">
            <HeartPulse className="text-brand"/> Tenants Health & Performance
          </h1>
          <p className="text-sm text-ink-soft">
            One-page view of every member tenant's data completeness, confidence, anomalies,
            initiatives and evidence. Click a row to drill into key measures.
          </p>
        </div>
        <input
          data-testid="period-input"
          placeholder="Period (e.g. 2025-Q4)"
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          className="border border-border rounded-md px-3 py-1.5 text-sm font-mono w-44"
        />
      </div>

      <div className="card overflow-hidden" data-testid="tenants-health-table">
        <table className="dense">
          <thead><tr>
            <th></th>
            <th>Tenant</th>
            <th>Country</th>
            <th className="text-right">FTE</th>
            <th>Health</th>
            <th className="text-right">Completeness</th>
            <th className="text-right">Approved KPIs</th>
            <th className="text-right">Avg confidence</th>
            <th className="text-right">Open anomalies</th>
            <th className="text-right">Initiatives</th>
            <th className="text-right">Evidence</th>
            <th className="text-right">Activity rows</th>
            <th></th>
          </tr></thead>
          <tbody>
            {data.rows.map((r) => {
              const band = HEALTH_BAND(r.health_score);
              const open = expanded === r.tenant.id;
              return (
                <React.Fragment key={r.tenant.id}>
                  <tr data-testid={`tenant-row-${r.tenant.id}`}
                    onClick={() => setExpanded(open ? null : r.tenant.id)}
                    className="cursor-pointer hover:bg-surface/60">
                    <td>{open ? <ChevronUp size={12}/> : <ChevronDown size={12}/>}</td>
                    <td>
                      <div className="font-medium">{r.tenant.name}</div>
                      <div className="text-[10px] text-ink-soft font-mono">{r.tenant.id}</div>
                    </td>
                    <td className="text-[11px] text-ink-soft">{r.tenant.country}</td>
                    <td className="num">{fmtNumber(r.tenant.headcount, 0)}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-1.5 bg-surface rounded-full overflow-hidden">
                          <div className="h-full" style={{width: `${r.health_score}%`, background: band.color}}/>
                        </div>
                        <span className={"pill " + band.pill + " text-[10px]"} data-testid={`band-${r.tenant.id}`}>{r.health_score}</span>
                      </div>
                    </td>
                    <td className="num">{r.completeness.completion_pct}%</td>
                    <td className="num text-[11px] text-ink-soft">{r.completeness.approved_kpis}/{r.completeness.expected_kpis}</td>
                    <td className="num">{(r.avg_confidence * 100).toFixed(0)}%</td>
                    <td className="num">
                      {r.open_anomalies > 0 && r.anomalies_high > 0 && <AlertTriangle size={11} className="inline text-status-error mr-1"/>}
                      {r.open_anomalies} <span className="text-[10px] text-ink-soft">({r.anomalies_high} hi)</span>
                    </td>
                    <td className="num">{r.initiatives_in_progress}/{r.initiatives_total}</td>
                    <td className="num">{r.evidence_count}</td>
                    <td className="num">{fmtNumber(r.activity_records, 0)}</td>
                    <td><ChevronRight size={12} className="text-ink-soft"/></td>
                  </tr>
                  {open && (
                    <tr><td colSpan={13} className="bg-surface/40 p-4">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <Drill label="Period" value={r.completeness.period}/>
                        <Drill label="Status" value={r.completeness.complete ? "Complete" : "Incomplete"}/>
                        <Drill label="Health score" value={`${r.health_score}/100 · ${band.label}`}/>
                        <Drill label="Baseline year" value={r.tenant.baseline_year}/>
                      </div>
                      <div className="mt-3 text-xs text-ink-soft">
                        Health = 50% × completeness + 30% × avg-confidence + 20% × (1 − anomaly-density).
                      </div>
                      <div className="mt-3 flex gap-2">
                        <a href={`/admin/database`} className="btn btn-secondary text-xs">Database manager</a>
                        <a href={`/platform/ibpap`} className="btn btn-secondary text-xs"><FileBarChart2 size={11}/> Consolidated rollup</a>
                      </div>
                    </td></tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Drill({ label, value }) {
  return (
    <div className="card-soft p-2">
      <div className="dlabel">{label}</div>
      <div className="font-mono mt-0.5">{value}</div>
    </div>
  );
}
